import api from './api';
import ErrorHandler from './errorHandler';

class PrintTemplateService {
    async list(params = {}) {
        try {
            return await api.get('/api/print-templates', { params });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Every template matching `params`. The server caps a page at 100, so a single
     * `list({ limit: 100 })` silently drops everything past the hundredth. This loads
     * page 1, then every other page in parallel, and returns one `{ data, pagination }`.
     *
     * `onProgress(data)` is called with the templates loaded so far, always as a
     * contiguous, in-order prefix — first with page 1, then again each time the next
     * page(s) in sequence have arrived — so a list can render at once and grow.
     */
    async listAll(params = {}, onProgress) {
        const limit = 100;
        const first = await this.list({ ...params, page: 1, limit });
        const totalPages = first?.pagination?.totalPages || 1;
        const out = [...(first?.data || [])];
        if (onProgress) onProgress(out.slice());
        if (totalPages <= 1) return { ...first, data: out };

        const slots = new Array(totalPages + 1).fill(null);
        let next = 2;
        await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) => i + 2).map(async (page) => {
                const res = await this.list({ ...params, page, limit });
                slots[page] = res?.data || [];
                let grew = false;
                while (next <= totalPages && slots[next]) {
                    out.push(...slots[next]);
                    next += 1;
                    grew = true;
                }
                if (grew && onProgress) onProgress(out.slice());
            }),
        );
        return { ...first, data: out };
    }

    /**
     * Persist the admin's hand-made order. `ids` is the order of the cards that
     * were on screen — a filtered view is fine, the server only rewrites the
     * slots those templates already held.
     */
    async reorder(ids) {
        try {
            return await api.patch('/api/print-templates/reorder', { ids });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** Ids of the print templates the current user has hearted. */
    async favorites() {
        try {
            return await api.get('/api/print-templates/favorites');
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async toggleFavorite(templateId) {
        try {
            return await api.post('/api/print-templates/favorites/toggle', { templateId });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async get(id) {
        try {
            return await api.get(`/api/print-templates/${id}`);
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async create(body) {
        try {
            return await api.post('/api/print-templates', body);
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async update(id, body) {
        try {
            return await api.put(`/api/print-templates/${id}`, body);
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async remove(id) {
        try {
            return await api.delete(`/api/print-templates/${id}`);
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async duplicate(id) {
        try {
            return await api.post(`/api/print-templates/${id}/duplicate`);
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** Deduct PRINT credits before exporting a template (user flow). */
    /**
     * Persist an exported file so it shows in Documents → Print Media.
     * The PNG/PDF are rendered in the browser, so they must be uploaded here —
     * the /export endpoint only charges credits and stores nothing.
     */
    async saveGenerated({ officeSpaceId, templateId, title, pngBlob, pdfBlob, thumbnail, width, height, pages = [] }) {
        try {
            const form = new FormData();
            if (officeSpaceId != null) form.append('officeSpaceId', String(officeSpaceId));
            if (templateId != null) form.append('templateId', String(templateId));
            if (title) form.append('title', title);
            if (thumbnail) form.append('thumbnail', thumbnail);
            if (width) form.append('width', String(width));
            if (height) form.append('height', String(height));
            if (pngBlob) form.append('png', pngBlob, `${title || 'print'}.png`);
            pages.slice(1).forEach((page, index) => {
                if (!page?.pngBlob) return;
                form.append(`png_${index + 1}`, page.pngBlob, `${title || 'print'}-${String(index + 2).padStart(2, '0')}.png`);
            });
            if (pages.length) {
                const pageMetadata = pages.map((page) => ({
                    frameId: page.frameId,
                    name: page.name,
                    width: page.width,
                    height: page.height,
                }));
                form.append('pages', JSON.stringify(pageMetadata));
            }
            if (pdfBlob) form.append('pdf', pdfBlob, `${title || 'print'}.pdf`);
            return await api.post('/api/print-media/generated', form);
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async listGenerated(params = {}) {
        try {
            return await api.get('/api/print-media/generated', { params });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** PNG + PDF as one ZIP, zipped server-side (see the route for why). */
    async downloadGeneratedZip(id, params = {}) {
        try {
            return await api.get(`/api/print-media/generated/${id}/download`, {
                params,
                responseType: 'blob',
            });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async deleteGenerated(id, params = {}) {
        try {
            return await api.delete(`/api/print-media/generated/${id}`, { params });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    // `checkOnly: true` = read-only "can the credit be paid?" before making the file; without it the credit is taken (call it AFTER the file exists).
    async authorizeExport(id, { officeSpaceId, format = 'png', checkOnly = false } = {}) {
        try {
            return await api.post(`/api/print-templates/${id}/export`, { officeSpaceId, format, ...(checkOnly ? { checkOnly: true } : {}) });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }
}

export default new PrintTemplateService();
